import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api/finance`;

export function fileUrl(path) {
  const token = localStorage.getItem("lx_token");
  const sep = path.includes("?") ? "&" : "?";
  return `${API}/${path}${sep}auth=${encodeURIComponent(token || "")}`;
}

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("lx_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && !window.location.pathname.includes("/login")) {
      localStorage.removeItem("lx_token");
      window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

export const CURRENCY_SYMBOLS = {
  TZS: "TSh", USD: "$", EUR: "€", GBP: "£", KES: "KSh", UGX: "USh",
  ZAR: "R", INR: "₹", CNY: "¥", JPY: "¥", AED: "AED", NGN: "₦",
};

export function curSym(currency) {
  if (!currency) return "";
  const code = String(currency).toUpperCase();
  return CURRENCY_SYMBOLS[code] || code;
}

export function fmtNum(n, currency) {
  if (n === null || n === undefined || n === "") return "—";
  const num = Number(n);
  if (Number.isNaN(num)) return "—";
  const formatted = num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (!currency) return formatted;
  return `${curSym(currency)} ${formatted}`;
}

export function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return d;
  }
}

// Turn any error into a clear, actionable message that explains WHY it happened
// and HOW to fix it. Accepts either an axios error object (preferred) or a raw
// FastAPI `detail` value (backwards compatible).
export function errMsg(input) {
  if (input && typeof input === "object" && (input.isAxiosError || input.response || input.request || input.config)) {
    return axiosErrorMessage(input);
  }
  return detailMessage(input);
}

function detailMessage(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    // FastAPI 422 validation errors: [{loc, msg, type}]
    return detail
      .map((e) => {
        if (typeof e === "string") return e;
        const field = Array.isArray(e?.loc) ? e.loc.filter((x) => x !== "body").join(".") : null;
        const msg = e?.msg || JSON.stringify(e);
        return field ? `${field}: ${msg}` : msg;
      })
      .join("; ");
  }
  if (detail?.msg) return detail.msg;
  if (detail instanceof Error) return detail.message;
  return String(detail);
}

const WHY = {
  400: "The request was invalid.",
  403: "You don't have permission to do this.",
  404: "We couldn't find that item — it may have been deleted or moved.",
  405: "That action isn't allowed here.",
  409: "This conflicts with data that already exists.",
  413: "The file is too large to upload.",
  415: "That file type isn't supported.",
  422: "Some of the details need attention.",
  429: "Too many attempts in a short time.",
};
const FIX = {
  400: "Please review what you entered and try again.",
  403: "If you need access, ask an administrator (e.g. Ronick or Finance).",
  404: "Refresh the page and try again.",
  405: "Refresh the page — the action may have moved.",
  409: "Reload to see the latest data, then retry.",
  413: "Upload a smaller file, or split it into parts.",
  415: "Use the expected format (e.g. a CSV exported from the template).",
  422: "Fix the highlighted fields and submit again.",
  429: "Please wait a few seconds and try again.",
};

function axiosErrorMessage(err) {
  const res = err.response;
  if (!res) {
    if (err.code === "ECONNABORTED") return "The request timed out — the server took too long. Check your connection and try again.";
    return "Can't reach the server. Check your internet connection and try again in a moment.";
  }
  const status = res.status;
  const hasDetail = res.data?.detail != null;
  const backend = hasDetail ? detailMessage(res.data.detail) : null;

  if (status === 401) return "Your session has expired. Please sign in again.";

  if (status >= 500) {
    if ([502, 503, 504].includes(status)) return "The server is temporarily unavailable. Please wait a few seconds and try again.";
    if (backend && backend !== "Internal Server Error") return backend;
    return "The server hit an unexpected problem while handling this request. Please try again in a moment — if it keeps happening, note what you were doing and contact support.";
  }

  // 4xx: the backend detail usually already explains the cause + fix — prefer it.
  if (backend) return backend;
  const why = WHY[status] || "The request could not be completed.";
  const fix = FIX[status] || "Please try again.";
  return `${why} ${fix}`;
}

export function openPdf(path) {
  const token = localStorage.getItem("lx_token");
  const sep = path.includes("?") ? "&" : "?";
  window.open(`${API}/${path}${sep}auth=${encodeURIComponent(token || "")}`, "_blank");
}
